import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  createComponent,
  DestroyRef,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getTailwindColorValue } from '@examdojo/styles';
import { BrowserJsPlumbInstance, newInstance } from '@jsplumb/browser-ui';
import { MarkdownModule } from 'ngx-markdown';
import { animationFrameScheduler, Subject, timer } from 'rxjs';
import { debounceTime, delay, filter, tap } from 'rxjs/operators';
import { AnnotationEngineComponent } from './annotation-engine';
import {
  MenuLabelOverlayComponent,
  RoundBoxLabelOverlayComponent,
  SquareBoxLabelOverlayComponent,
  TextOnlyLabelOverlayComponent,
} from './label-engine';
import { getLatexLines, measureWidthBeforeAlignChar } from './super-latex-alignment.util';
import { AlignGroupMember, SuperLatexAlignGroupService } from './super-latex-align-group.service';
import type {
  HighlightConfig,
  SuperLatexAnnotation,
  SuperLatexConnection,
  SuperLatexConnectionStyle,
  SuperLatexContent,
  SuperLatexNode,
} from './super-latex.model';

interface AnnotationTarget {
  annotation: SuperLatexAnnotation;
  element: HTMLElement;
}

@Component({
  selector: 'dojo-super-latex',
  standalone: true,
  imports: [MarkdownModule, AnnotationEngineComponent, NgStyle],
  templateUrl: './super-latex.component.html',
  styleUrls: ['./super-latex.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Animations disabled - using CSS transitions instead for smoother experience
  animations: [],
})
export class SuperLatexComponent implements AfterViewInit, OnDestroy, AlignGroupMember {
  constructor() {
    effect(() => {
      const container = this.container();
      if (!container) {
        return;
      }

      const nodeIds = this.latexNodes()
        .map((n: SuperLatexNode) => n.id ?? n.latex)
        .join('|');

      // Use untracked to read lastNodeIds without creating a dependency
      const lastIds = untracked(() => this.lastNodeIds());
      const structureChanged = nodeIds !== lastIds;
      this.lastNodeIds.set(nodeIds);

      this.annotations();
      this.setupAnnotations$$.next();

      if (structureChanged) {
        const inGroup = !!untracked(() => this.alignGroup());

        if (inGroup) {
          // In a group: skip internal alignment, let the service handle it
          this.isAligning.set(false);
          this.alignGroupService.notifyContentChanged(untracked(() => this.alignGroup())!);
          this.waitForKatexRender(() => this.fitLatexToContainer());
        } else if (this.alignOnEquals()) {
          this.isAligning.set(true);
          this.calculateEqualsAlignment(() => {
            this.fitLatexToContainer();
            if (this.connections().length > 0) {
              this.alignmentComplete$$.next();
            } else {
              this.isAligning.set(false);
            }
          });
        } else {
          this.isAligning.set(false);
          if (this.connections().length > 0) {
            this.renderConnections$$.next();
          }
          this.waitForKatexRender(() => this.fitLatexToContainer());
        }
      }
    });

    // Register/unregister with align group service
    effect(() => {
      const groupName = this.alignGroup();
      const prevGroup = untracked(() => this.registeredGroup());

      if (prevGroup && prevGroup !== groupName) {
        this.alignGroupService.unregister(prevGroup, this);
      }

      if (groupName) {
        this.registeredGroup.set(groupName);
        this.alignGroupService.register(groupName, this);
      } else {
        this.registeredGroup.set(null);
      }
    });

    effect(() => {
      const connections = this.connections();

      // Always delete existing connections immediately for instant visual feedback
      this.jsPlumbInstance?.deleteEveryConnection();

      if (connections.length === 0) {
        this.marginMap.set(new Map());
      } else {
        this.renderConnections$$.next();
      }
    });

    effect(() => {
      const highlightedAnchors = this.highlightedAnchors();
      this.focusAnchors(); // Track as dependency
      this.applyHighlights$$.next(highlightedAnchors);
    });

    // Repaint jsPlumb connections when highlight toggles, since the padding change shifts anchor positions
    effect(() => {
      this.highlight(); // Track as dependency
      if (this.jsPlumbInstance) {
        setTimeout(() => this.jsPlumbInstance?.repaintEverything(), 50);
      }
    });

    // Separate effect for block alignment — runs whenever alignTextBlock changes
    // without requiring a structural change to latex nodes
    effect(() => {
      const containerEl = this.container()?.nativeElement;
      if (!containerEl) {
        return;
      }

      const alignTextBlock = this.alignTextBlock();
      containerEl.style.alignItems =
        alignTextBlock === 'left' ? 'flex-start' : alignTextBlock === 'center' ? 'center' : 'flex-end';
    });

    this.setupResizeListener();
    this.setupRenderPipelines();
  }

  // prettier-ignore
  private static readonly _TAILWIND_SAFELIST = [
    'text-amber-500', 'text-teal-500', 'text-green-500', 'text-sky-500', 'text-indigo-500',
    'text-pink-500', 'text-red-500', 'text-orange-500', 'text-purple-500', 'text-neutral-500',
    'bg-amber-50', 'bg-teal-50', 'bg-green-50', 'bg-sky-50', 'bg-indigo-50',
    'bg-pink-50', 'bg-red-50', 'bg-orange-50', 'bg-purple-50', 'bg-neutral-50',
    'bg-amber-100',
  ];

  /* -------------------------------------------------------------------------- */
  /*                               State & Inputs                               */
  /* -------------------------------------------------------------------------- */
  // Core state signals for alignment and structure tracking
  private readonly isAligning = signal(false);
  private readonly lastNodeIds = signal('');

  // Dependencies
  private readonly destroyRef = inject(DestroyRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly alignGroupService = inject(SuperLatexAlignGroupService);

  // Render triggers
  private readonly resize$$ = new Subject<void>();
  private readonly renderConnections$$ = new Subject<void>();
  private readonly setupAnnotations$$ = new Subject<void>();
  private readonly applyHighlights$$ = new Subject<Record<string, HighlightConfig>>();
  private readonly alignmentComplete$$ = new Subject<void>();

  // DOM References and Input Signals
  readonly container = viewChild<ElementRef>('container');
  readonly linesWrapper = viewChild<ElementRef>('linesWrapper');
  readonly content = input.required<SuperLatexContent>();
  readonly padding = input<string>('0');
  readonly fontSize = input<string | undefined>(undefined);

  /* -------------------------------------------------------------------------- */
  /*                               Computed State                               */
  /* -------------------------------------------------------------------------- */
  // Derived state from input content for easier template access
  readonly latexNodes = computed(() => this.content().latex_nodes);
  readonly annotations = computed(() => this.content().annotations ?? []);
  readonly connections = computed(() => this.content().connections ?? []);
  readonly alignGroup = computed(() => this.content().layout?.align_group ?? null);
  private readonly registeredGroup = signal<string | null>(null);
  readonly alignTextBlock = computed(() => this.content().layout?.align_text_block ?? 'center');
  readonly alignText = computed(() => this.content().layout?.align_text ?? 'center');
  readonly alignOn = computed<string | null>(() => {
    const layout = this.content().layout;
    if (layout?.align_on) {
      return layout.align_on;
    }
    if (layout?.align_on_equals ?? true) {
      return '=';
    }
    return null;
  });
  readonly alignOnEquals = this.alignOn;
  readonly highlightedAnchors = computed(() => this.content().highlighted_anchors ?? {});
  readonly highlight = computed(() => this.content().layout?.highlight ?? false);
  readonly dimmedNodes = computed(() => new Set(this.content().dimmed_nodes ?? []));
  readonly focusAnchors = computed(() => new Set(this.content().focus_anchors ?? []));

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
    trust: true,
  };

  readonly annotationTargets = signal<AnnotationTarget[]>([]);
  private readonly marginMap = signal<Map<string, { top: number; bottom: number }>>(new Map());
  private readonly paddingMap = signal<Map<string, number>>(new Map());

  private jsPlumbInstance?: BrowserJsPlumbInstance;
  private visibilityObserver?: ResizeObserver;

  readonly lineStyles = computed(() => {
    const nodes = this.latexNodes();
    const alignOnEquals = this.alignOnEquals();
    const textAlign = alignOnEquals ? 'left' : this.alignText();
    const paddingMap = this.paddingMap();
    const marginMap = this.marginMap();

    return nodes.map((line: SuperLatexNode) => {
      const resultStyle: Record<string, string> = {
        'text-align': textAlign,
      };

      const nodeKey = line?.id ?? line?.latex;
      const calculatedPadding = nodeKey ? paddingMap.get(nodeKey) : undefined;
      const padding = line?.align_on_equals_padding_left ?? calculatedPadding;

      if (padding && alignOnEquals && padding > 0) {
        resultStyle['padding-left'] = `${padding}px`;
      }

      const calculatedMargin = nodeKey ? marginMap.get(nodeKey) : undefined;
      const marginTop = line?.collision_margin_top ?? calculatedMargin?.top;
      const marginBottom = line?.collision_margin_bottom ?? calculatedMargin?.bottom;

      if (marginTop && marginTop > 0) {
        resultStyle['margin-top'] = `${marginTop}px`;
      }
      if (marginBottom && marginBottom > 0) {
        resultStyle['margin-bottom'] = `${marginBottom}px`;
      }

      return resultStyle;
    });
  });

  ngAfterViewInit(): void {
    this.resize$$.next();
    this.setupVisibilityObserver();
  }

  ngOnDestroy(): void {
    this.jsPlumbInstance?.destroy();
    this.visibilityObserver?.disconnect();

    const group = this.registeredGroup();
    if (group) {
      this.alignGroupService.unregister(group, this);
    }
  }

  repaint(): void {
    this.jsPlumbInstance?.repaintEverything();
  }

  /* ---------- AlignGroupMember interface ---------- */

  getContainerElement(): HTMLElement | undefined {
    return this.container()?.nativeElement;
  }

  getAlignChar(): string | null {
    return this.alignOn();
  }

  private waitForKatexRender(onComplete: () => void, attempt = 0): void {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      return;
    }

    const latexLines = getLatexLines(containerEl);
    const hasRendered = latexLines.length > 0 && latexLines.every((line) => line.querySelector('.katex-html'));

    if (!hasRendered && attempt < 30) {
      timer(100)
        .pipe(
          tap(() => this.waitForKatexRender(onComplete, attempt + 1)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }

    if (hasRendered) {
      Promise.all([document.fonts.ready, new Promise(requestAnimationFrame)]).then(() => {
        onComplete();
      });
    }
  }

  /**
   * Uses ResizeObserver to detect when the component gets dimensions
   * (e.g., when a parent toggle opens). Triggers alignment if it hasn't succeeded yet.
   */
  private setupVisibilityObserver(): void {
    if (!this.alignOnEquals()) {
      return;
    }

    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      return;
    }

    this.visibilityObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      // Recalculate alignment when container gets dimensions and alignment hasn't succeeded
      if (entry && entry.contentRect.width > 0 && this.paddingMap().size === 0) {
        const group = this.registeredGroup();
        if (group) {
          this.alignGroupService.notifyContentChanged(group);
        } else {
          this.calculateEqualsAlignment();
        }
      }
    });

    this.visibilityObserver.observe(containerEl);
  }

  private setupRenderPipelines(): void {
    // Connection rendering pipeline
    this.renderConnections$$
      .pipe(
        // Debounce rapid emissions
        debounceTime(16),
        // Wait for next animation frame to let DOM update
        delay(0, animationFrameScheduler),
        tap(() => {
          this.calculateNodeMargins();
        }),
        // If currently aligning, don't proceed immediately
        filter(() => !this.isAligning()),
        // Wait for margin CSS transition (250ms) to complete
        delay(280),
        tap(() => {
          if (!this.jsPlumbInstance) {
            this.initializeJsPlumb();
          }
          this.setupAnnotations();
          this.renderConnections();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Alignment completion pipeline
    this.alignmentComplete$$
      .pipe(
        // Wait for CSS animations to complete
        delay(250),
        tap(() => {
          this.isAligning.set(false);
          if (!this.jsPlumbInstance) {
            this.initializeJsPlumb();
          }
          this.setupAnnotations();
          this.renderConnections();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Annotations pipeline
    this.setupAnnotations$$
      .pipe(
        delay(0, animationFrameScheduler),
        tap(() => this.setupAnnotations()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Highlights pipeline
    this.applyHighlights$$
      .pipe(
        delay(0, animationFrameScheduler),
        tap((highlightedAnchors) => this.applyHighlights(highlightedAnchors)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /* -------------------------------------------------------------------------- */
  /*                         Connection Engine (jsPlumb)                        */
  /* -------------------------------------------------------------------------- */
  // Manages drawing lines/arrows between elements using jsPlumb library.
  // Handles initialization, rendering, and cleanup of connections.

  private initializeJsPlumb(): void {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      return;
    }

    this.jsPlumbInstance?.destroy();
    this.jsPlumbInstance = newInstance({ container: containerEl });
  }

  private renderConnections(): void {
    const containerEl = this.container()?.nativeElement;
    const instance = this.jsPlumbInstance;
    if (!containerEl || !instance) {
      return;
    }

    instance.deleteEveryConnection();

    this.connections().forEach((connection) => {
      const startElement = containerEl.querySelector(`[data-anchor="${connection.from_anchor}"]`) as HTMLElement | null;
      const endElement = containerEl.querySelector(`[data-anchor="${connection.to_anchor}"]`) as HTMLElement | null;

      if (!startElement || !endElement) {
        return;
      }

      const connectionStyle = connection.style ?? {};
      const overlays: Array<{ type: string; options: Record<string, unknown> }> = [];
      const arrowStyle = connectionStyle.arrow_style ?? 'filled';

      if (arrowStyle !== 'none') {
        const color = this.getConnectionColor(connectionStyle);
        overlays.push({
          type: 'Arrow',
          options:
            arrowStyle === 'open'
              ? {
                  location: 1,
                  width: 4,
                  length: 4,
                  foldback: 0.4,
                  paintStyle: { stroke: color, strokeWidth: 1, fill: 'none' },
                }
              : { location: 1, width: 6, length: 6, foldback: 1 },
        });
      }

      if (connectionStyle.label?.text) {
        this.addLabelOverlay(connection.id, connectionStyle, overlays);
      }

      instance.connect({
        source: startElement,
        target: endElement,
        anchors: [connectionStyle.from_socket ?? 'Center', connectionStyle.to_socket ?? 'Center'],
        connector: connectionStyle.connector_type === 'Straight'
          ? { type: 'Straight' as const, options: {} }
          : connectionStyle.connector_type === 'Bezier'
            ? { type: 'Bezier' as const, options: {} }
            : { type: 'Flowchart' as const, options: { cornerRadius: 15, stub: connectionStyle.stub ?? 20 } },
        paintStyle: {
          stroke: this.getConnectionColor(connectionStyle),
          strokeWidth: connectionStyle.width ?? 2,
          dashstyle: this.getLineDashStyle(connectionStyle.line_style),
        },
        endpoint: 'Blank',
        overlays,
      });
    });
  }

  private addLabelOverlay(
    connectionId: string,
    connectionStyle: NonNullable<SuperLatexConnection['style']>,
    overlays: Array<{ type: string; options: Record<string, unknown> }>,
  ): void {
    const label = connectionStyle.label;
    if (!label?.text) {
      return;
    }

    const labelStyle = label.style ?? 'square-box';

    overlays.push({
      type: 'Custom',
      options: {
        create: () => {
          let ComponentClass:
            | typeof RoundBoxLabelOverlayComponent
            | typeof MenuLabelOverlayComponent
            | typeof SquareBoxLabelOverlayComponent
            | typeof TextOnlyLabelOverlayComponent;
          if (labelStyle === 'round-box') {
            ComponentClass = RoundBoxLabelOverlayComponent;
          } else if (labelStyle === 'menu') {
            ComponentClass = MenuLabelOverlayComponent;
          } else if (labelStyle === 'text-only') {
            ComponentClass = TextOnlyLabelOverlayComponent;
          } else {
            ComponentClass = SquareBoxLabelOverlayComponent;
          }

          const componentRef = createComponent(ComponentClass as never, {
            environmentInjector: this.environmentInjector,
          });

          componentRef.setInput('text', label.text);
          componentRef.setInput('color', connectionStyle.color ?? 'sky');
          if (label.offset) {
            componentRef.setInput('offset', label.offset);
          }

          componentRef.changeDetectorRef.detectChanges();

          return componentRef.location.nativeElement;
        },
        location: label.location ?? 0.5,
        id: `label-${connectionId}`,
      },
    });
  }

  private getConnectionColor(connectionStyle: SuperLatexConnectionStyle): string {
    const cssVar = getTailwindColorValue(connectionStyle.color ?? 'sky', connectionStyle.color_shade ?? 500);
    // jsPlumb SVG strokes may not resolve CSS var() — compute the actual value
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(`--${connectionStyle.color ?? 'sky'}-${connectionStyle.color_shade ?? 500}`).trim();
    return resolved || cssVar;
  }

  private getLineDashStyle(lineStyle?: 'solid' | 'dashed' | 'dotted'): string | undefined {
    if (!lineStyle || lineStyle === 'solid') {
      return undefined;
    }
    if (lineStyle === 'dashed') {
      return '4 2';
    }
    if (lineStyle === 'dotted') {
      return '1 2';
    }
    return undefined;
  }

  /* -------------------------------------------------------------------------- */
  /*                        Annotation & Highlight Engine                       */
  /* -------------------------------------------------------------------------- */
  // Manages overlays (tooltips, boxes) and text highlighting.
  // Finds target elements in the DOM and attaches annotation components.

  private setupAnnotations(): void {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      this.annotationTargets.set([]);
      return;
    }

    const targets = this.annotations()
      .map((annotation) => {
        const element = containerEl.querySelector(`[data-anchor="${annotation.target_anchor}"]`) as HTMLElement;
        return element ? { annotation, element } : null;
      })
      .filter((target): target is AnnotationTarget => target !== null);

    this.annotationTargets.set(targets);
  }

  private applyHighlights(highlightedAnchors: Record<string, HighlightConfig>): void {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      return;
    }

    const focusAnchors = this.focusAnchors();
    const elements = containerEl.querySelectorAll('[data-anchor]');

    // Clear previous highlight classes
    elements.forEach((element: Element) => {
      const el = element as HTMLElement;
      const previousClasses = el.getAttribute('data-highlight-classes');
      if (previousClasses) {
        const classesToRemove = previousClasses.split(' ');
        if (classesToRemove.length > 0) {
          el.classList.remove(...classesToRemove);
        }
        el.removeAttribute('data-highlight-classes');
      }
    });

    // Reset previously modified inline colors
    containerEl.querySelectorAll('[data-original-color]').forEach((el: Element) => {
      const originalColor = el.getAttribute('data-original-color');
      if (originalColor) {
        (el as HTMLElement).style.color = originalColor;
      }
      el.removeAttribute('data-original-color');
    });

    // Apply highlights
    if (highlightedAnchors && Object.keys(highlightedAnchors).length > 0) {
      Object.entries(highlightedAnchors).forEach(([anchor, config]) => {
        const element = containerEl.querySelector(`[data-anchor="${anchor}"]`) as HTMLElement;
        if (element) {
          const classes = this.convertHighlightConfigToClasses(config);
          if (classes.length > 0) {
            element.classList.add(...classes);
            element.setAttribute('data-highlight-classes', classes.join(' '));
          }
        }
      });
    }

    // Process focus mode: remove colors from non-focus elements on focus lines
    if (focusAnchors.size > 0) {
      // First, collect all focus anchor elements
      const focusAnchorElements: HTMLElement[] = [];
      focusAnchors.forEach((anchor) => {
        const el = containerEl.querySelector(`[data-anchor="${anchor}"]`) as HTMLElement;
        if (el) {
          focusAnchorElements.push(el);
        }
      });

      // Find all elements with inline color styles on non-dimmed lines
      const coloredElements = containerEl.querySelectorAll('.latex-line:not(.dimmed) [style*="color"]');

      coloredElements.forEach((el: Element) => {
        const htmlEl = el as HTMLElement;

        // Skip transparent colors (used by KaTeX for phantom elements)
        if (htmlEl.style.color === 'transparent') {
          return;
        }

        // Skip phantom elements (used for alignment, should stay invisible)
        // Check both: element is inside a phantom OR element contains a phantom
        if (htmlEl.closest('.mphantom') || htmlEl.querySelector('.mphantom')) {
          return;
        }

        // Check if this element is inside any focus anchor
        const isInsideFocusAnchor = focusAnchorElements.some((anchorEl) => anchorEl.contains(htmlEl));

        if (!isInsideFocusAnchor && htmlEl.style.color) {
          // Store original color and remove it
          htmlEl.setAttribute('data-original-color', htmlEl.style.color);
          htmlEl.style.color = 'inherit';
        }
      });
    }
  }

  private convertHighlightConfigToClasses(config: HighlightConfig): string[] {
    const classes: string[] = [];

    switch (config.style) {
      case 'text-color':
        if (config.color) {
          classes.push(`text-${config.color}-500`);
        }
        break;
      case 'text-color-with-background':
        if (config.color) {
          classes.push(`text-${config.color}-500`, `bg-${config.color}-50`);
        }
        break;
      case 'highlighter':
        classes.push('bg-amber-100');
        break;
    }

    return classes;
  }

  /* -------------------------------------------------------------------------- */
  /*                       Layout Engine (Resize & Scaling)                     */
  /* -------------------------------------------------------------------------- */
  // Handles responsive behavior:
  // 1. Detects container resizing
  // 2. Scales down font size to fit container
  // 3. Enables horizontal scrolling if scaling limit is reached

  private setupResizeListener(): void {
    this.resize$$
      .pipe(
        debounceTime(100),
        takeUntilDestroyed(),
        tap(() => {
          this.waitForKatexRender(() => {
            this.fitLatexToContainer();
            this.jsPlumbInstance?.repaintEverything();
          });
        }),
      )
      .subscribe();

    const resizeHandler = () => this.resize$$.next();
    window.addEventListener('resize', resizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', resizeHandler));
  }

  private fitLatexToContainer(): void {
    const containerEl = this.container()?.nativeElement;
    const wrapperEl = this.linesWrapper()?.nativeElement;
    if (!containerEl || !wrapperEl) {
      return;
    }

    const latexLines = getLatexLines(containerEl);
    // Account for wrapper padding in width calculation
    const wrapperStyle = window.getComputedStyle(wrapperEl);
    const paddingLeft = parseFloat(wrapperStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(wrapperStyle.paddingRight) || 0;
    const containerWidth = containerEl.clientWidth - paddingLeft - paddingRight;
    const lineStyles = this.lineStyles();
    const alignTextBlock = this.alignTextBlock();

    // Reset container alignment
    containerEl.style.alignItems =
      alignTextBlock === 'left' ? 'flex-start' : alignTextBlock === 'center' ? 'center' : 'flex-end';
    containerEl.style.removeProperty('overflow-x');

    latexLines.forEach((line, index) => {
      line.style.removeProperty('font-size');
      line.style.removeProperty('line-height');
      line.style.whiteSpace = 'nowrap';

      // Restore original text-align
      const originalStyle = lineStyles[index];
      if (originalStyle && originalStyle['text-align']) {
        line.style.textAlign = originalStyle['text-align'];
      } else {
        line.style.removeProperty('text-align');
      }
    });

    if (latexLines.length === 0) {
      return;
    }

    let scaleFactor = 1;

    for (const line of latexLines) {
      const contentWidth = line.scrollWidth;

      if (contentWidth > containerWidth) {
        const scale = containerWidth / contentWidth;
        if (scale < scaleFactor) {
          scaleFactor = scale;
        }
      }
    }

    if (scaleFactor < 1) {
      const computedStyle = window.getComputedStyle(latexLines[0]);
      const baseFontSize = parseFloat(computedStyle.fontSize) || 16;
      const MIN_FONT_SIZE = 12;
      const minScaleLimit = MIN_FONT_SIZE / baseFontSize;

      let enableScroll = false;

      if (scaleFactor < minScaleLimit) {
        scaleFactor = minScaleLimit;
        enableScroll = true;
      }

      if (scaleFactor > 1) {
        scaleFactor = 1;
      }

      const safeScale = scaleFactor * 0.99;

      latexLines.forEach((line) => {
        const lineStyle = window.getComputedStyle(line);
        const currentFontSize = parseFloat(lineStyle.fontSize);
        const currentLineHeight = parseFloat(lineStyle.lineHeight);

        line.style.fontSize = `${currentFontSize * safeScale}px`;
        if (!isNaN(currentLineHeight)) {
          line.style.lineHeight = `${currentLineHeight * safeScale}px`;
        }
      });

      if (this.alignOnEquals()) {
        this.applyEqualsAlignment(latexLines);
      }

      if (enableScroll) {
        containerEl.style.overflowX = 'auto';
        containerEl.style.alignItems = 'flex-start';
      }
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                   Alignment Engine (Equals Sign Alignment)                 */
  /* -------------------------------------------------------------------------- */
  // Aligns multiple lines of LaTeX equations based on their equals ('=') signs.
  // Measures DOM elements to calculate necessary left-padding for each line.

  private calculateEqualsAlignment(onComplete?: () => void): void {
    if (!this.alignOnEquals()) {
      onComplete?.();
      return;
    }

    this.waitForKatexAndAlign(0, onComplete);
  }

  private waitForKatexAndAlign(attempt = 0, onComplete?: () => void): void {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      return;
    }

    const expectedNodes = this.latexNodes();
    const latexLines = getLatexLines(containerEl);

    const expectedKeys = new Set(expectedNodes.map((n: SuperLatexNode) => n.id ?? n.latex));
    const domKeys = new Set(
      Array.from(latexLines)
        .map((line) => line.getAttribute('data-node-key'))
        .filter((key): key is string => key !== null),
    );

    const allExpectedInDOM = expectedNodes.every((node: SuperLatexNode) => {
      const nodeKey = node.id ?? node.latex;
      return domKeys.has(nodeKey);
    });

    // Filter to only expected lines (exclude any stale elements)
    const expectedLines = latexLines.filter((line) => {
      const key = line.getAttribute('data-node-key');
      return key && expectedKeys.has(key);
    });

    const hasRendered = expectedLines.every((line) => line.querySelector('.katex-html'));

    if ((!allExpectedInDOM || !hasRendered) && attempt < 20) {
      timer(50)
        .pipe(
          tap(() => this.waitForKatexAndAlign(attempt + 1, onComplete)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }

    if (allExpectedInDOM && hasRendered) {
      Promise.all([document.fonts.ready, new Promise(requestAnimationFrame)]).then(() => {
        // Only align expected lines, exclude leaving elements
        const freshLatexLines = getLatexLines(containerEl).filter((line) => {
          const key = line.getAttribute('data-node-key');
          return key && expectedKeys.has(key);
        });
        this.applyEqualsAlignment(freshLatexLines);
        onComplete?.();
      });
    } else {
      console.warn('SuperLatex: Alignment timed out waiting for DOM/Animations');
      onComplete?.();
    }
  }

  private applyEqualsAlignment(latexLines: HTMLElement[]): void {
    const widthsByKey = new Map<string, number>();

    latexLines.forEach((line) => {
      const nodeKey = line.getAttribute('data-node-key');
      if (!nodeKey) {
        return;
      }

      const width = this.measureWidthBeforeAlignCharInternal(line);
      widthsByKey.set(nodeKey, width);
    });

    const maxWidth = Math.max(0, ...Array.from(widthsByKey.values()));

    const newPaddingMap = new Map<string, number>();
    this.latexNodes().forEach((node: SuperLatexNode) => {
      const nodeKey = node.id ?? node.latex;
      const width = widthsByKey.get(nodeKey) ?? 0;
      const leftPadding = maxWidth - width;

      if (leftPadding > 0) {
        newPaddingMap.set(nodeKey, leftPadding);
      }
    });

    this.paddingMap.set(newPaddingMap);
  }

  private measureWidthBeforeAlignCharInternal(lineElement: HTMLElement): number {
    const alignChar = this.alignOn();
    if (!alignChar) {
      return 0;
    }
    return measureWidthBeforeAlignChar(lineElement, alignChar);
  }

  /* -------------------------------------------------------------------------- */
  /*                     Margin Calculation to avoid collisions                 */
  /* -------------------------------------------------------------------------- */
  // Calculates extra spacing (margins) between lines to prevent connection lines/arrows
  // from overlapping with text or other elements.

  private calculateNodeMargins(): void {
    const connections = this.connections();
    const nodes = this.latexNodes();

    const topConnectionTargets = new Map<number, Set<number>>();
    const bottomConnectionTargets = new Map<number, Set<number>>();

    nodes.forEach((node: SuperLatexNode, nodeIndex: number) => {
      const topTargets = new Set<number>();
      const bottomTargets = new Set<number>();

      connections.forEach((conn) => {
        const nodeHasFromAnchor = node.latex.includes(`anchor=${conn.from_anchor}`);
        const nodeHasToAnchor = node.latex.includes(`anchor=${conn.to_anchor}`);

        if (nodeHasFromAnchor) {
          const targetNodeIndex = nodes.findIndex((n: SuperLatexNode) => n.latex.includes(`anchor=${conn.to_anchor}`));
          if (conn.style?.from_socket === 'Bottom' && targetNodeIndex !== -1) {
            bottomTargets.add(targetNodeIndex);
          }
          if (conn.style?.from_socket === 'Top' && targetNodeIndex !== -1) {
            topTargets.add(targetNodeIndex);
          }
        }

        if (nodeHasToAnchor) {
          const sourceNodeIndex = nodes.findIndex((n: SuperLatexNode) =>
            n.latex.includes(`anchor=${conn.from_anchor}`),
          );
          if (conn.style?.to_socket === 'Bottom' && sourceNodeIndex !== -1) {
            bottomTargets.add(sourceNodeIndex);
          }
          if (conn.style?.to_socket === 'Top' && sourceNodeIndex !== -1) {
            topTargets.add(sourceNodeIndex);
          }
        }
      });

      if (topTargets.size > 0) {
        topConnectionTargets.set(nodeIndex, topTargets);
      }
      if (bottomTargets.size > 0) {
        bottomConnectionTargets.set(nodeIndex, bottomTargets);
      }
    });

    const newMarginMap = new Map<string, { top: number; bottom: number }>();

    nodes.forEach((node: SuperLatexNode, index: number) => {
      const topTargets = topConnectionTargets.get(index);
      const bottomTargets = bottomConnectionTargets.get(index);

      const topMargin = !topTargets ? 0 : topTargets.size === 1 && !topTargets.has(index) ? 20 : 40;
      const bottomMargin = !bottomTargets ? 0 : bottomTargets.size === 1 && !bottomTargets.has(index) ? 20 : 40;

      if (topMargin > 0 || bottomMargin > 0) {
        const nodeKey = node.id ?? node.latex;
        newMarginMap.set(nodeKey, { top: topMargin, bottom: bottomMargin });
      }
    });

    this.marginMap.set(newMarginMap);
  }
}
